import type { DependencyList } from 'react';
import { useRef, useState } from 'react';

/**
 * Recall the value of an expression at the time of the previous render.
 *
 * This is a more idiomatic alternative to the hook provided by react-use. That version of the hook
 * works using useEffect, which can produce unexpected results when a state setter is called from
 * the render function.
 *
 * Note that this hook will cause components using it to re-render one additional time if the
 * passed value changes, which can result in infinite re-rendering if a non-memoized value is
 * passed.
 */
export function usePrevious<T>(value: T): T {
  const [prev, setPrev] = useState(() => value);
  if (value !== prev) setPrev(() => value);
  return prev;
}

/**
 * A hook that calls a given function whenever one or more values change, but never on the initial
 * render.
 *
 * This is a more idiomatic alternative to misusing useEffect to perform some action whenever a
 * value changes. For simple cases, you may want to use usePrevious instead.
 *
 * The dependency list is compared using the same rules as other dependency lists in React; values
 * are only considered the same if they are equal according to ===.
 *
 * To access the previous dependencies in the callback, you may need to use `as const` when defining
 * the dependency list.
 */
export function useOnChange<T extends DependencyList, R>(
  fn: (prev: T) => R,
  deps: T
): {
  /** Whether one or more dependencies changed on this render. */
  changed: boolean;
  /** The most recent return value of the callback, if any. */
  result: R | null;
} {
  const [prevDeps, setPrevDeps] = useState(deps);
  const [result, setResult] = useState<R | null>(null);

  if (deps.length !== prevDeps.length || deps.some((value, i) => value !== prevDeps[i])) {
    const result = fn(prevDeps);
    setResult(result);
    setPrevDeps(deps);
    return { changed: true, result };
  }

  return { changed: false, result };
}

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(() => value);
  const [updateTimer, setUpdateTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  if (value !== debouncedValue && !updateTimer) {
    setUpdateTimer(
      setTimeout(() => {
        setDebouncedValue(latestValueRef.current);
        setUpdateTimer(null);
      }, delay)
    );
  }

  return debouncedValue;
}
